import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import BackendUnavailableScreen from "./components/BackendUnavailableScreen";
import BackendWakingBanner from "./components/BackendWakingBanner";
import PermissionRoute from "./components/PermissionRoute";
import Spinner from "./components/Spinner";
import { useAuthStore } from "./stores/authStore";

// Layouts
import AuthLayout from "./layouts/AuthLayout";
import MainLayout from "./layouts/MainLayout";

// Eagerly loaded pages (small / critical path)
import Login from "./pages/auth/Login";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import Dashboard from "./pages/Dashboard";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages (larger, non-critical)
const Budget = lazy(() => import("./pages/Budget"));
const BudgetPlanner = lazy(() => import("./pages/BudgetPlanner"));
const Bundles = lazy(() => import("./pages/Bundles"));
const AiChat = lazy(() => import("./pages/AiChat"));
const Career = lazy(() => import("./pages/Career"));
const DocumentQueue = lazy(() => import("./pages/DocumentQueue"));
const Documents = lazy(() => import("./pages/Documents"));
const Family = lazy(() => import("./pages/Family"));
const Payments = lazy(() => import("./pages/Payments"));
const HOACharges = lazy(() => import("./pages/HOACharges"));
const Properties = lazy(() => import("./pages/Properties"));
const BuildingDetail = lazy(() => import("./pages/BuildingDetail"));
const ApartmentDetail = lazy(() => import("./pages/ApartmentDetail"));
const Finances = lazy(() => import("./pages/Finances"));
const Settings = lazy(() => import("./pages/Settings"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Knowledge = lazy(() => import("./pages/Knowledge"));
const UtilityMigrations = lazy(() => import("./pages/UtilityMigrations"));

function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <Spinner />
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isBackendUnavailable, refreshToken } =
    useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isBackendUnavailable && refreshToken) {
    return <BackendUnavailableScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const hydrateAuth = useAuthStore((state) => state.hydrateAuth);

  useEffect(() => {
    hydrateAuth();
  }, [hydrateAuth]);

  return (
    <>
      <BackendWakingBanner />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Route>

      {/* Protected routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard */}
        <Route path="/" element={<PermissionRoute><Dashboard /></PermissionRoute>} />

        {/* Documents */}
        <Route path="/documents" element={<PermissionRoute><Documents /></PermissionRoute>} />
        <Route path="/documents/queue" element={<PermissionRoute><DocumentQueue /></PermissionRoute>} />
        <Route path="/bundles" element={<PermissionRoute><Bundles /></PermissionRoute>} />

        {/* Properties */}
        <Route path="/properties" element={<PermissionRoute><Properties /></PermissionRoute>} />
        <Route path="/properties/buildings/:id" element={<PermissionRoute><BuildingDetail /></PermissionRoute>} />
        <Route path="/properties/apartments/:id" element={<PermissionRoute><ApartmentDetail /></PermissionRoute>} />
        <Route path="/utility-migrations" element={<PermissionRoute><UtilityMigrations /></PermissionRoute>} />

        {/* Family */}
        <Route path="/family" element={<PermissionRoute><Family /></PermissionRoute>} />

        {/* Payments */}
        <Route path="/payments" element={<PermissionRoute><Payments /></PermissionRoute>} />
        <Route path="/payments/hoa" element={<PermissionRoute><HOACharges /></PermissionRoute>} />

        {/* Budget */}
        <Route path="/budget" element={<PermissionRoute><Budget /></PermissionRoute>} />
        <Route path="/budget/planner" element={<PermissionRoute><BudgetPlanner /></PermissionRoute>} />
        <Route
          path="/estate-planner"
          element={<Navigate to="/budget/planner" replace />}
        />

        {/* Tasks */}
        <Route path="/tasks" element={<PermissionRoute><Tasks /></PermissionRoute>} />

        {/* Finances */}
        <Route path="/finances" element={<PermissionRoute><Finances /></PermissionRoute>} />

        {/* Career */}
        <Route path="/career" element={<PermissionRoute><Career /></PermissionRoute>} />

        {/* AI Chat */}
        <Route path="/ai-chat" element={<PermissionRoute><AiChat /></PermissionRoute>} />

        {/* Knowledge / second brain */}
        <Route path="/knowledge" element={<PermissionRoute><Knowledge /></PermissionRoute>} />

        {/* Settings */}
        <Route path="/profile/access" element={<Navigate to="/settings?tab=accessRequests" replace />} />
        <Route path="/admin/access-requests" element={<Navigate to="/settings?tab=accessRequests" replace />} />
        <Route path="/settings" element={<PermissionRoute><Settings /></PermissionRoute>} />

        {/* Authenticated 404 — keep shell (sidebar + header) */}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Unauthenticated 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
    </>
  );
}
